/**
 * Traduce al español los mensajes de validación que devuelve el back.
 *
 * NestJS valida con class-validator, que arma sus mensajes en inglés y con el
 * nombre crudo de la propiedad. Pintados tal cual salen cosas como:
 *
 *   tipoContrato must be one of the following values: tiempo_completo,
 *   medio_tiempo, por_proyecto, por_horas
 *
 * …que ni se lee en español ni le dice nada a quien captura: esos valores son
 * las llaves internas del catálogo, no lo que aparece en el desplegable.
 *
 * Esto vivía dentro de `HireForm` como una tabla de reemplazos que corría
 * `String.replace` sobre todo el texto con `/gi`. Eso traducía la frase a
 * pedazos y dejaba pasar cualquier patrón que no estuviera en la tabla —el de
 * «one of the following values» entre ellos—. Aquí se reconoce la regla
 * completa y se reescribe entera.
 */

/** Nombre de cada propiedad del expediente tal como la nombra la pantalla. */
const CAMPOS: Record<string, string> = {
  areaId: 'Departamento o área',
  numeroPlazas: 'Número de vacantes',
  // Decisiones y entrevistas de candidatos (SRH-333): el servidor ya manda estos
  // avisos en español, pero uno viejo o de otra ruta puede traer el nombre crudo.
  reason: 'Motivo',
  motivo: 'Motivo',
  motivoRechazo: 'Motivo del rechazo',
  comment: 'Comentario',
  comentario: 'Comentario',
  comments: 'Comentarios',
  comentarios: 'Comentarios',
  decision: 'Decisión',
  result: 'Resultado',
  modality: 'Modalidad',
  locationOrLink: 'Lugar o enlace',
  interviewDate: 'Fecha de la entrevista',
  interviewTime: 'Hora de la entrevista',
  interviewerUserId: 'Entrevistador',
  puestoId: 'Puesto',
  puesto: 'Puesto',
  sueldoBrutoMensual: 'Sueldo bruto mensual',
  fechaIngreso: 'Fecha de ingreso',
  tipoContrato: 'Tipo de contrato',
  jornada: 'Jornada',
  curp: 'CURP',
  rfc: 'RFC',
  nss: 'NSS',
  fechaNacimiento: 'Fecha de nacimiento',
  sexo: 'Sexo',
  estadoCivil: 'Estado civil',
  telefonoPersonal: 'Teléfono personal',
  direccion: 'Dirección',
  jefeInmediatoId: 'Jefe inmediato',
  prestaciones: 'Prestaciones',
  emergenciaContactoNombre: 'Nombre del contacto de emergencia',
  emergenciaContactoTelefono: 'Teléfono del contacto de emergencia',
  emergenciaContactoRelacion: 'Relación del contacto de emergencia',
  firstName: 'Nombre',
  lastName: 'Apellidos',
  email: 'Correo',
}

/**
 * Valores de catálogo, para no enseñar la llave interna.
 *
 * Son los mismos textos de los desplegables: si el error nombra una opción, hay
 * que poder buscarla en la lista tal cual se lee ahí.
 */
const VALORES: Record<string, string> = {
  tiempo_completo: 'Tiempo completo',
  medio_tiempo: 'Medio tiempo',
  por_proyecto: 'Por proyecto',
  por_horas: 'Por horas',
  reducida: 'Reducida',
  mixta: 'Mixta',
  nocturna: 'Nocturna',
  masculino: 'Masculino',
  femenino: 'Femenino',
  soltero: 'Soltero',
  casado: 'Casado',
  divorciado: 'Divorciado',
  viudo: 'Viudo',
  union_libre: 'Unión libre',
}

const conMayusculaInicial = (texto: string) =>
  texto.charAt(0).toUpperCase() + texto.slice(1)

/** `tiempo_completo` → `Tiempo completo`, aunque no esté en el catálogo. */
const comoEtiqueta = (valor: string): string => {
  const limpio = valor.trim()
  return VALORES[limpio] ?? conMayusculaInicial(limpio.replace(/_/g, ' '))
}

/** `tipoContrato` → `Tipo de contrato`; `fechaIngreso` → `Fecha ingreso`. */
const comoCampo = (nombre: string): string => {
  const limpio = nombre.trim()
  if (CAMPOS[limpio]) return CAMPOS[limpio]

  // Ya venía en español (o es un nombre que no conocemos): se respeta.
  if (/\s/.test(limpio)) return limpio

  return conMayusculaInicial(
    limpio.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase(),
  )
}

/**
 * Reglas de class-validator. Se prueban en orden y gana la primera que empate,
 * así que las más específicas van arriba.
 */
const REGLAS: Array<[RegExp, (grupos: string[]) => string]> = [
  [
    /^(.+?) must be one of the following values:\s*(.+)$/i,
    ([campo, valores]) =>
      `${comoCampo(campo)} debe ser uno de: ${valores
        .split(',')
        .map(comoEtiqueta)
        .join(', ')}`,
  ],
  [
    /^(.+?) should not be empty$/i,
    ([campo]) => `${comoCampo(campo)} es obligatorio`,
  ],
  [
    /^(.+?) must be a string$/i,
    ([campo]) => `${comoCampo(campo)} debe ser texto`,
  ],
  [
    /^(.+?) must be an email$/i,
    ([campo]) => `${comoCampo(campo)} no tiene un formato válido`,
  ],
  [
    /^(.+?) must be a (valid ISO 8601 )?date( string)?$/i,
    ([campo]) => `${comoCampo(campo)} debe ser una fecha válida`,
  ],
  [
    /^(.+?) must be an integer number$/i,
    ([campo]) => `${comoCampo(campo)} debe ser un número entero`,
  ],
  [
    /^(.+?) must be a number conforming to the specified constraints$/i,
    ([campo]) => `${comoCampo(campo)} debe ser un número válido`,
  ],
  [
    /^(.+?) must be a positive number$/i,
    ([campo]) => `${comoCampo(campo)} debe ser mayor que cero`,
  ],
  [
    /^(.+?) must not be less than (.+)$/i,
    ([campo, limite]) => `${comoCampo(campo)} no puede ser menor que ${limite}`,
  ],
  [
    /^(.+?) must not be greater than (.+)$/i,
    ([campo, limite]) => `${comoCampo(campo)} no puede ser mayor que ${limite}`,
  ],
  [
    /^(.+?) must be longer than or equal to (\d+) characters$/i,
    ([campo, n]) => `${comoCampo(campo)} debe tener al menos ${n} caracteres`,
  ],
  [
    /^(.+?) must be shorter than or equal to (\d+) characters$/i,
    ([campo, n]) => `${comoCampo(campo)} no debe pasar de ${n} caracteres`,
  ],
  [
    /^(.+?) must be a UUID$/i,
    ([campo]) => `${comoCampo(campo)} no es un identificador válido`,
  ],
  // Los rechazos de permisos del servidor (SRH-322) traen la lista de claves
  // internas que faltan; a quien los lee no le sirven de nada.
  [
    /^Insufficient permissions\b/i,
    () => 'No tienes permiso para realizar esta acción',
  ],
  [
    /^Access denied\. Required roles\b/i,
    () => 'No tienes permiso para realizar esta acción',
  ],
  [
    /^You do not have permission to access this Job Request$/i,
    () => 'No tienes permiso para ver o modificar esta solicitud',
  ],
  [
    /^property (.+?) should not exist$/i,
    ([campo]) => `${comoCampo(campo)} no se puede enviar en este formulario`,
  ],
]

/** Mensajes que no traen campo y llegan de la capa HTTP, no de la validación. */
const GENERICOS: Record<string, string> = {
  'bad request': 'La información enviada no es válida',
  unauthorized: 'Tu sesión no es válida',
  forbidden: 'No tienes permiso para hacer esto',
  'not found': 'No se encontró lo que buscabas',
  'internal server error': 'Error del servidor. Intenta de nuevo.',
  'validation failed': 'La información enviada no es válida',
}

/** Traduce un renglón. Lo que no se reconoce se devuelve igual. */
export const mensajeDeValidacion = (mensaje: string): string => {
  const texto = mensaje.trim()
  if (!texto) return texto

  const generico = GENERICOS[texto.toLowerCase()]
  if (generico) return generico

  for (const [patron, arma] of REGLAS) {
    const empate = texto.match(patron)
    if (empate) return arma(empate.slice(1).map((g) => g ?? ''))
  }

  return texto
}

/**
 * Traduce lo que venga del cliente HTTP: una cadena, el arreglo de renglones
 * de NestJS, o el error completo.
 *
 * Quita repetidos porque un mismo campo puede romper dos reglas a la vez —no es
 * texto y está vacío— y las dos terminan diciendo lo mismo.
 */
export const mensajesDeValidacion = (
  entrada: unknown,
  respaldo = 'Ocurrió un error. Intenta de nuevo.',
): string => {
  if (entrada == null) return respaldo

  if (Array.isArray(entrada)) {
    const renglones = [
      ...new Set(
        entrada
          .map((m) => mensajeDeValidacion(String(m)))
          .filter((m) => m !== ''),
      ),
    ]
    return renglones.length > 0 ? renglones.join('. ') : respaldo
  }

  if (typeof entrada === 'object') {
    const { message } = entrada as { message?: unknown }
    return message == null ? respaldo : mensajesDeValidacion(message, respaldo)
  }

  const traducido = mensajeDeValidacion(String(entrada))
  return traducido === '' ? respaldo : traducido
}
